import { z } from 'zod'

/**
 * Access-control management shapes — WorkOS environment roles and permissions, as the
 * `/api/rbac/*` routes expose them.
 *
 * These are projections of WorkOS's objects, not mirrors: the app database never stores roles or
 * permissions. WorkOS owns them, puts the claims in the session JWT, and this contract only
 * exists so the management page can create and arrange them. That's also why there is no
 * `accountId` anywhere here — environment roles are workspace-wide by definition.
 */

/**
 * The permission that gates access management itself — both the `/api/rbac/*` routes and the
 * `/app/access` nav entry key off this one slug. Shared here so the API's gate and the SPA's nav
 * can never disagree about its spelling. The slug is Kaushik's choice (created 2026-08-11); if it
 * is ever renamed in WorkOS, this constant is the single place to follow it.
 */
export const MANAGE_ACCESS_PERMISSION = 'manage-all:users'

/**
 * Managing the PEOPLE in your own organization — invite, change a member's role, remove, and
 * issue temporary grants. Split from {@link MANAGE_ACCESS_PERMISSION} because those are two very
 * different privileges wearing one slug: editing environment roles and permissions rewrites what
 * every organization's roles mean, while managing members only ever touches your own tenant
 * (every route scopes to the session's organization and 404s foreign ids).
 *
 * That difference is what makes delegation possible — a client admin can eventually hold this
 * without gaining the ability to edit the role definitions they're assigning. Until assignable-
 * role bounds exist, though, this stays with internal admins: nothing yet stops a holder from
 * inviting someone into an internal role.
 */
export const ORG_USERS_MANAGE_PERMISSION = 'org-users:manage'

/**
 * Org-wide authority over every account — create them, and reach any one without being assigned
 * to it individually.
 *
 * This is the escape hatch that makes resource gating practical rather than crippling. Internal
 * admins are supposed to see everything; expressing that as an assignment on every account would
 * mean thousands of rows to maintain and one forgotten row away from an admin locked out of a
 * customer. Holding this short-circuits the resource check entirely — which also means it costs
 * no metered FGA call, so the people who touch the most accounts generate the least traffic.
 *
 * The flip side is that it is genuinely powerful: it is the one permission that sees sensitive
 * accounts without being named on them. It belongs to internal admins and nobody else.
 */
export const MANAGE_ALL_ACCOUNTS_PERMISSION = 'manage-all:accounts'
/**
 * Manage the SHARED template library — the templates that belong to no account.
 *
 * Org-wide rather than per-resource because a shared template belongs to no account, so there is
 * no resource to check it against. That makes it coarse platform power, which by the standing rule
 * can never be handed to a customer's admin: a customer edits its own account's templates through
 * the per-account FGA path instead.
 */
export const TEMPLATES_MANAGE_PERMISSION = 'templates:manage'

/**
 * Curate report issues — open the day's draft, pull coverage, edit, move and remove articles, and
 * send (CLI-695). Org-wide for now: the editor is the intelligence associates' daily workspace and
 * every associate works across accounts, so the JWT path (no metered check) is the right one.
 * When customers get the editor, per-sub-account access rides the existing `subaccount:edit`
 * FGA check beside this rather than a second org-wide slug.
 */
export const REPORTS_CURATE_PERMISSION = 'reports:curate'

/**
 * Triage, edit, configure and send real-time alerts (CLI-744). Org-wide for the same reason as
 * `reports:curate`: an IA runs several clients' RTAs at once, all day, and the JWT path costs no
 * metered check. A new slug rather than reusing the report one, because who watches alerts and who
 * builds reports are different rotas — and a slug is environment config, so it must be
 * provisioned (and attached to the IA roles) in staging AND production before the page opens.
 */
export const RTAS_CURATE_PERMISSION = 'rtas:curate'

/**
 * The permission a resource-scoped grant of edit access carries (CLI-602): assigned per-account
 * through WorkOS FGA role assignments, never as an org-wide role. Account-scoped, so it rides in
 * FGA checks rather than the session JWT's `permissions` claim.
 */
export const ACCOUNT_EDIT_PERMISSION = 'account:edit'

/** Read access to one account, the resource-scoped counterpart of {@link ACCOUNT_EDIT_PERMISSION}. */
export const ACCOUNT_VIEW_PERMISSION = 'account:view'

/**
 * The FGA resource type accounts are registered under. Dashboard-configured and immutable, which
 * is why it's a constant rather than a value anything computes: the slug in WorkOS, the slug the
 * reconciler registers with, and the slug a check names all have to be the same string.
 */
export const ACCOUNT_RESOURCE_TYPE = 'account'

/**
 * The FGA resource type sub-accounts are registered under, with `account` as its parent — which
 * is what makes granular grants and the agency case the same mechanism. A role held on the
 * account cascades to every sub-account beneath it; a role held on one sub-account reaches only
 * that one. Dashboard-configured and immutable, like every resource type.
 */
export const SUBACCOUNT_RESOURCE_TYPE = 'subaccount'

/** Read one sub-account. Reached by cascade from the account, or granted on the sub-account. */
export const SUBACCOUNT_VIEW_PERMISSION = 'subaccount:view'

/** Edit one sub-account — the granular counterpart of account:edit. */
export const SUBACCOUNT_EDIT_PERMISSION = 'subaccount:edit'

/**
 * Manage who else can reach ONE account — the resource-scoped counterpart of
 * {@link ORG_USERS_MANAGE_PERMISSION}, and the thing that makes delegation safe.
 *
 * The org-wide slug can't be given to a customer's admin: it reaches every tenant's people and
 * has no bound on which roles a holder may hand out, so granting it to a client admin would also
 * let them invite somebody into an internal role. This one is held ON an account, so it reaches
 * exactly the people on that account — and grants made with it are bounded to capabilities the
 * grantor holds there themselves (see the escalation guard in routes/resource-access.ts).
 */
export const ACCOUNT_MANAGE_ACCESS_PERMISSION = 'account:manage-access'

/** Manage who can reach one sub-account. Reached by cascade from the account, or granted directly. */
export const SUBACCOUNT_MANAGE_ACCESS_PERMISSION = 'subaccount:manage-access'

/**
 * A permission this codebase depends on existing in a WorkOS environment.
 *
 * `resourceType` is our word, not WorkOS's wire value: `'organization'` is an ordinary
 * environment-wide permission (created without a resource type; carried in the session JWT's
 * `permissions` claim), `'account'` is a resource-scoped permission checked through FGA against
 * one account.
 */
export interface ManagedPermission {
  slug: string
  name: string
  description?: string
  resourceType: 'organization' | 'account' | 'subaccount'
}

/** A role's minimum permission set — see {@link ROLE_ATTACHMENTS}. */
export interface RoleAttachment {
  roleSlug: string
  /** Display name, used when the sync has to create the role. */
  name: string
  /**
   * Which resource type the role can be assigned on. Omitted for organization-wide roles like
   * `admin`, which WorkOS already provides and the sync never creates.
   */
  resourceType?: 'account' | 'subaccount'
  mustInclude: string[]
}

/**
 * What a person can be granted, in the words an admin actually uses.
 *
 * WorkOS has no way to give somebody a permission directly — a role is the only carrier, and
 * per-user permission assignment is on their roadmap rather than shipped. That's a fact about
 * their model, not a concept our users should have to learn, so the UI talks in capabilities and
 * never says "role".
 *
 * A capability is NOT one permission. "Can edit" carries `account:edit` AND `subaccount:edit`,
 * because FGA only inherits when a role scoped to the parent holds the child type's permissions —
 * a role carrying `account:edit` alone would grant nothing on the account's sub-accounts, and the
 * agency case would quietly stop working. The capability is the thing a person means; the
 * permissions are how far it reaches.
 *
 * One capability maps to a different role per resource type, which is why `roles` is a list:
 * granting "can edit" on an account and on a sub-account are the same intent carried by two
 * different WorkOS objects.
 */
export interface Capability {
  id: string
  label: string
  description: string
  roles: { resourceType: string; roleSlug: string }[]
  /**
   * Capabilities this one already contains. Not enforcement — the containment is real in the
   * role's permission set (an editor role carries `account:view`) — but the UI needs to know, or
   * it would show "can view" unchecked next to a granted "can edit" and invite an admin to grant
   * something they already have twice.
   */
  implies?: string[]
  /**
   * The floor: what being on the access list means at all, granted to everyone who is added and
   * never offered as a choice. Giving somebody access to an account and then asking whether they
   * should be able to see it is not a real question — the only real question is what they may do
   * beyond looking. Exactly one capability per resource type should carry this.
   *
   * It is granted as its own assignment even when a larger capability is picked, so that removing
   * the extra leaves access rather than silently removing the person.
   */
  baseline?: boolean
}

export const CAPABILITIES: Capability[] = [
  {
    id: 'view',
    label: 'Can view',
    description: 'See this and everything beneath it.',
    baseline: true,
    roles: [
      { resourceType: ACCOUNT_RESOURCE_TYPE, roleSlug: 'account-viewer' },
      { resourceType: SUBACCOUNT_RESOURCE_TYPE, roleSlug: 'subaccount-viewer' },
    ],
  },
  {
    id: 'edit',
    label: 'Can edit',
    description: 'Change configuration. Includes viewing.',
    roles: [
      { resourceType: ACCOUNT_RESOURCE_TYPE, roleSlug: 'account-editor' },
      { resourceType: SUBACCOUNT_RESOURCE_TYPE, roleSlug: 'subaccount-editor' },
    ],
    implies: ['view'],
  },
  {
    id: 'manage-access',
    label: 'Can manage access',
    description: 'Add and remove other people here. Includes editing.',
    roles: [
      { resourceType: ACCOUNT_RESOURCE_TYPE, roleSlug: 'account-access-manager' },
      { resourceType: SUBACCOUNT_RESOURCE_TYPE, roleSlug: 'subaccount-access-manager' },
    ],
    // Implies edit, and so transitively view. Someone who can decide who else gets in can
    // already give themselves anything below it; pretending otherwise would be a bound that
    // looks like security and isn't.
    implies: ['edit'],
  },
]

/** Every capability that applies to one resource type, baseline included. */
export function capabilitiesForResourceType(resourceType: string): Capability[] {
  return CAPABILITIES.filter((c) => c.roles.some((r) => r.resourceType === resourceType))
}

/**
 * What being added to this resource means on its own — see {@link Capability.baseline}. Every
 * grant includes it, which is why it is not among the choices a checkbox list offers.
 */
export function baselineCapability(resourceType: string): Capability | undefined {
  return capabilitiesForResourceType(resourceType).find((c) => c.baseline)
}

/** The capabilities worth asking about — everything beyond the baseline. */
export function optionalCapabilities(resourceType: string): Capability[] {
  return capabilitiesForResourceType(resourceType).filter((c) => !c.baseline)
}

/**
 * Everything holding `capabilityId` gets you, itself included — the transitive closure of
 * `implies`. Written as a walk rather than a one-level read so a later three-tier capability
 * doesn't silently lose the bottom tier.
 */
export function impliedCapabilities(capabilityId: string): string[] {
  const seen = new Set<string>()
  const queue = [capabilityId]
  while (queue.length > 0) {
    const id = queue.shift()
    if (id === undefined || seen.has(id)) continue
    seen.add(id)
    const capability = CAPABILITIES.find((c) => c.id === id)
    if (capability?.implies) queue.push(...capability.implies)
  }
  return [...seen]
}

/** The role that carries a capability on one resource type, or undefined if it doesn't apply. */
export function roleForCapability(capabilityId: string, resourceType: string): string | undefined {
  return CAPABILITIES.find((c) => c.id === capabilityId)?.roles.find(
    (r) => r.resourceType === resourceType,
  )?.roleSlug
}

/**
 * The permissions a managed role must carry — the checked-in minimum from {@link ROLE_ATTACHMENTS}.
 *
 * Used by the escalation guard: a grantor may only hand out a capability whose permissions they
 * already hold on that resource, and this is what "its permissions" means. Empty for a role the
 * manifest doesn't manage, which the guard treats as ungrantable rather than unbounded.
 */
export function permissionsForRole(roleSlug: string): string[] {
  return ROLE_ATTACHMENTS.find((a) => a.roleSlug === roleSlug)?.mustInclude ?? []
}

/** The capability a role slug carries — the reverse lookup, for presenting assignments. */
export function capabilityForRole(roleSlug: string): Capability | undefined {
  return CAPABILITIES.find((c) => c.roles.some((r) => r.roleSlug === roleSlug))
}

/**
 * Whether a role is organization-wide — assignable to a membership itself, rather than held on
 * one resource. Current WorkOS payloads spell this `resourceTypeSlug: 'organization'` (the root
 * of the resource-type tree); older ones omitted the field, which the API projects as ''. Both
 * mean the same thing, and this is the one place that knows it — an org-role picker built on a
 * bare `=== ''` comparison offered nothing at all against a live environment.
 */
export function isOrgWideRole(role: { resourceTypeSlug: string }): boolean {
  return role.resourceTypeSlug === '' || role.resourceTypeSlug === 'organization'
}

/**
 * Every permission slug the code gates on — the source of truth that
 * `apps/api/scripts/sync-permissions.ts` reconciles each WorkOS environment against.
 *
 * A slug the code checks is only half the story: the permission must also exist in the WorkOS
 * environment, or the JWT never carries it and every user 403s — and nothing fails loudly when
 * that provisioning step is skipped. Add the slug here in the same PR that gates on it, then run
 * the sync once per environment (staging AND production) before deploying.
 */
export const PERMISSION_MANIFEST: ManagedPermission[] = [
  {
    slug: MANAGE_ACCESS_PERMISSION,
    name: 'Manage access',
    description: 'Gates the /api/rbac routes and the access page.',
    resourceType: 'organization',
  },
  {
    slug: ORG_USERS_MANAGE_PERMISSION,
    name: 'Manage organization users',
    description: "Invite, re-role and remove members of one's own organization; issue grants.",
    resourceType: 'organization',
  },
  {
    slug: MANAGE_ALL_ACCOUNTS_PERMISSION,
    name: 'Manage all accounts',
    description: 'Create accounts, and reach any account without a per-account assignment.',
    resourceType: 'organization',
  },
  {
    slug: TEMPLATES_MANAGE_PERMISSION,
    name: 'Manage shared templates',
    description: 'Create and edit templates in the shared library, which belong to no account.',
    resourceType: 'organization',
  },
  {
    slug: REPORTS_CURATE_PERMISSION,
    name: 'Curate report issues',
    description: 'Open, pull, edit and send report issues in the report editor.',
    resourceType: 'organization',
  },
  {
    slug: RTAS_CURATE_PERMISSION,
    name: 'Curate real-time alerts',
    description: 'Triage, edit, configure and send real-time alerts (RTAs).',
    resourceType: 'organization',
  },
  {
    slug: ACCOUNT_EDIT_PERMISSION,
    name: 'Edit account',
    description: 'Edit one account — granted per-resource through FGA role assignments (CLI-602).',
    resourceType: 'account',
  },
  {
    slug: ACCOUNT_VIEW_PERMISSION,
    name: 'View account',
    description: "Read one account's data — the resource-scoped read counterpart of account:edit.",
    resourceType: 'account',
  },
  {
    slug: SUBACCOUNT_VIEW_PERMISSION,
    name: 'View sub-account',
    description: 'Read one sub-account — by cascade from the account, or granted directly.',
    resourceType: 'subaccount',
  },
  {
    slug: SUBACCOUNT_EDIT_PERMISSION,
    name: 'Edit sub-account',
    description: 'Edit one sub-account without gaining access to its siblings.',
    resourceType: 'subaccount',
  },
  {
    slug: ACCOUNT_MANAGE_ACCESS_PERMISSION,
    name: 'Manage access to account',
    description: "Add and remove people on one account, bounded by the grantor's own access.",
    resourceType: 'account',
  },
  {
    slug: SUBACCOUNT_MANAGE_ACCESS_PERMISSION,
    name: 'Manage access to sub-account',
    description: 'Add and remove people on one sub-account.',
    resourceType: 'subaccount',
  },
]

/**
 * Role wiring the sync enforces — MINIMUMS, applied strictly additively.
 *
 * Each entry reads "this role, where it exists, must include at least these permissions": the
 * sync adds what's missing and never removes anything, so everything else about the role is
 * untouched. The full arrangement — which roles exist, what else they carry, who holds them —
 * stays a runtime/admin concern on the access page. A role listed here that doesn't exist in the
 * environment is reported as a manual step, not created: role creation stays deliberate.
 */
/**
 * The org-wide role a person outside Clipbook holds, and it carries nothing.
 *
 * WorkOS has no concept of a membership without a role — `OrganizationMembership.role` is
 * required, and a membership created without one lands on the organization's default. So "no
 * platform role" cannot be expressed by absence; it has to be expressed by a role that grants
 * nothing. This is that role.
 *
 * Its emptiness is the entire specification. Every org-wide permission is carried in the session
 * JWT and reaches every tenant in the environment, so a customer's admin must hold none of them;
 * their real access is FGA grants on their own account. Adding a permission here would hand it to
 * every external user of every customer at once.
 *
 * It earns its place by being the thing a directory can filter on: "external" is
 * `role === CLIENT_ROLE`, answerable without reading a single FGA assignment.
 */
export const CLIENT_ROLE = 'client'

/**
 * The intelligence associates' role: the daily job without the keys to the platform. Staff who
 * run the platform hold `admin`; everyone outside Clipbook holds {@link CLIENT_ROLE}.
 *
 * A middle `clipbook-admin` tier (people management without the role editor) existed until
 * CLI-847 and was removed unheld in every environment — a role nobody holds is a manifest entry,
 * a WorkOS object, and a permission set someone has to keep in step for nothing.
 *
 * What this role does NOT do is make "sensitive account" enforceable. That needs an associate who
 * does not hold the account bypass, which in turn needs a way to grant them the accounts they do
 * work — WorkOS groups, which require Directory Sync (none is connected). Dropping the bypass
 * before then would take every account away from every associate at once.
 */
export const CLIPBOOK_ASSOCIATE_ROLE = 'clipbook-associate'

export const ROLE_ATTACHMENTS: RoleAttachment[] = [
  // Admin holds both halves of the split gate — separating them is about what can be delegated
  // to others, not about narrowing the internal admin who could grant themselves either anyway.
  {
    roleSlug: 'admin',
    name: 'Admin',
    mustInclude: [
      MANAGE_ACCESS_PERMISSION,
      ORG_USERS_MANAGE_PERMISSION,
      MANAGE_ALL_ACCOUNTS_PERMISSION,
      TEMPLATES_MANAGE_PERMISSION,
      REPORTS_CURATE_PERMISSION,
      RTAS_CURATE_PERMISSION,
    ],
  },
  // Deliberately empty — see CLIENT_ROLE. The sync reconciles it like any other managed role,
  // which means an accidental permission added to it in the dashboard is reported as drift.
  {
    roleSlug: CLIENT_ROLE,
    name: 'Client',
    mustInclude: [],
  },
  // The daily job and nothing else: open every account's editor, curate reports and RTAs, send.
  // No people management, no role editor, no shared template library. `manage-all:accounts` stays
  // for now because removing it without groups would take every account away at once.
  {
    roleSlug: CLIPBOOK_ASSOCIATE_ROLE,
    name: 'Clipbook associate',
    mustInclude: [
      MANAGE_ALL_ACCOUNTS_PERMISSION,
      REPORTS_CURATE_PERMISSION,
      RTAS_CURATE_PERMISSION,
    ],
  },
  // Account-scoped roles carry the CHILD type's permissions too, and that is the whole cascade
  // mechanism: inheritance in FGA is a role on a parent containing permissions for child types,
  // not something the hierarchy does on its own. Without these two lines, granting someone the
  // account would leave them unable to open any of its sub-accounts — the agency case broken in
  // the least obvious way.
  {
    roleSlug: 'account-viewer',
    name: 'Account viewer',
    resourceType: 'account',
    mustInclude: [ACCOUNT_VIEW_PERMISSION, SUBACCOUNT_VIEW_PERMISSION],
  },
  // Editing implies reading: checks name one permission, so an editor lacking account:view would
  // be able to write an account it can't open. The implication lives here, not in the check.
  {
    roleSlug: 'account-editor',
    name: 'Account editor',
    resourceType: 'account',
    mustInclude: [
      ACCOUNT_EDIT_PERMISSION,
      ACCOUNT_VIEW_PERMISSION,
      SUBACCOUNT_EDIT_PERMISSION,
      SUBACCOUNT_VIEW_PERMISSION,
    ],
  },
  // Sub-account-scoped roles, for granting one client of an agency without its siblings.
  {
    roleSlug: 'subaccount-viewer',
    name: 'Sub-account viewer',
    resourceType: 'subaccount',
    mustInclude: [SUBACCOUNT_VIEW_PERMISSION],
  },
  {
    roleSlug: 'subaccount-editor',
    name: 'Sub-account editor',
    resourceType: 'subaccount',
    mustInclude: [SUBACCOUNT_EDIT_PERMISSION, SUBACCOUNT_VIEW_PERMISSION],
  },
  // Access managers — a customer's own admin, bounded to their account. Each carries everything
  // the capability it serves implies (manage-access → edit → view), because the checked-in
  // manifest test fails otherwise: the UI would show access the FGA check denies.
  {
    roleSlug: 'account-access-manager',
    name: 'Account access manager',
    resourceType: 'account',
    mustInclude: [
      ACCOUNT_MANAGE_ACCESS_PERMISSION,
      ACCOUNT_EDIT_PERMISSION,
      ACCOUNT_VIEW_PERMISSION,
      SUBACCOUNT_MANAGE_ACCESS_PERMISSION,
      SUBACCOUNT_EDIT_PERMISSION,
      SUBACCOUNT_VIEW_PERMISSION,
    ],
  },
  {
    roleSlug: 'subaccount-access-manager',
    name: 'Sub-account access manager',
    resourceType: 'subaccount',
    mustInclude: [
      SUBACCOUNT_MANAGE_ACCESS_PERMISSION,
      SUBACCOUNT_EDIT_PERMISSION,
      SUBACCOUNT_VIEW_PERMISSION,
    ],
  },
]

/**
 * WorkOS slug conventions: lowercase, and permissions read as `resource:action`
 * (`reports:read`). The colon is meaningful to convention but not to WorkOS, so one pattern
 * serves roles (no colon by custom) and permissions alike. This is a UX guardrail; WorkOS
 * remains the authority and its rejections surface as 4xx with its own message.
 */
export const rbacSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z0-9][a-z0-9:_.-]*$/,
    'must be lowercase and start alphanumeric; then letters, digits, ":", "_", ".", "-"',
  )

export const permissionSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** WorkOS built-ins. They can't be deleted, and the UI shouldn't offer to. */
  system: z.boolean(),
  createdAt: z.string(),
})

export const roleSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Permission slugs, not ids — matching what arrives in the session JWT's claims. */
  permissions: z.array(z.string()),
  /**
   * Which resource type this role can be assigned on; empty for org-wide roles.
   *
   * Load-bearing for the UI, not decoration: offering an org-wide role like `member` in a
   * per-account grant picker means offering something that grants nothing, and it is the first
   * option people click.
   */
  resourceTypeSlug: z.string(),
  createdAt: z.string(),
})

export const createPermissionSchema = z.object({
  slug: rbacSlugSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
})

export const createRoleSchema = z.object({
  slug: rbacSlugSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  /** Assigned right after creation; empty is fine — permissions can be arranged later. */
  permissions: z.array(rbacSlugSchema).max(100).default([]),
})

/** Replaces the role's whole permission set — PUT semantics, so unchecking is just omission. */
export const setRolePermissionsSchema = z.object({
  permissions: z.array(rbacSlugSchema).max(100),
})

export const listPermissionsResponseSchema = z.object({
  permissions: z.array(permissionSchema),
})

export const listRolesResponseSchema = z.object({
  roles: z.array(roleSchema),
})

export type Permission = z.infer<typeof permissionSchema>
export type Role = z.infer<typeof roleSchema>
export type CreatePermission = z.infer<typeof createPermissionSchema>
export type CreateRole = z.infer<typeof createRoleSchema>
export type SetRolePermissions = z.infer<typeof setRolePermissionsSchema>
export type ListPermissionsResponse = z.infer<typeof listPermissionsResponseSchema>
export type ListRolesResponse = z.infer<typeof listRolesResponseSchema>
